import type { ReactElement } from 'react';

import { RefreshIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { TruncatedText } from '../../TruncatedText';

interface TableErrorRowProps {
    message: string;
    onRetry: () => void;
    /** The table's error row height, so the virtualiser's reserved space matches what renders. */
    height: number;
    /** The table's inline and block spacings, keeping the row aligned with its neighbours. */
    spacings: {
        inline: number | Record<string, number>;
        block: number | Record<string, number>;
    };
}

/** Replaces the placeholder rows of a failed page, pinned to the taller error row height. */
const TableErrorRow = ({
    message,
    onRetry: handleRetry,
    height,
    spacings,
}: TableErrorRowProps): ReactElement => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            paddingInline: spacings.inline,
            paddingBottom: spacings.block,
            height,
            overflow: 'hidden',
            boxSizing: 'border-box',
        }}
    >
        <Alert
            severity="warning"
            sx={{ flexGrow: 1, minWidth: 0 }}
            slotProps={{
                message: {
                    sx: {
                        flexGrow: 1,
                        // Alert lets its message scroll by default, which beats the ellipsis.
                        minWidth: 0,
                        overflow: 'hidden',
                    },
                },
            }}
            icon={false}
        >
            <Stack
                direction="row"
                spacing={5}
                sx={{ alignItems: 'center' }}
                useFlexGap
            >
                <Button
                    color="warning"
                    startIcon={<RefreshIcon />}
                    sx={{ flexShrink: 0 }}
                    onClick={handleRetry}
                >
                    Retry
                </Button>

                <TruncatedText variant="inherit">{message}</TruncatedText>
            </Stack>
        </Alert>
    </Box>
);

export { TableErrorRow };
export type { TableErrorRowProps };
